import { sheetDate, sheetItems } from './orderSheet';
import { packCount } from './packWords';
import { saveOrderPdf } from './orderPdf';

// Sending an order to its supplier on WhatsApp (owner, 2026-10-27). A page can neither see nor
// drive another tab, and a WhatsApp link carries text but never a file, so the button does the two
// things it can: it opens the supplier's chat in WhatsApp Web, in the browser staff are already
// signed in to, with the order typed out to send; and it saves the sheet Print makes as a PDF, to
// drag into the chat when the supplier wants the sheet too. WhatsApp opens in a tab of one name, so
// the next order goes to that same tab rather than a new one.

export const WHATSAPP_TAB = 'pamh-whatsapp';

const isPhoneOrTablet = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

/** The order as a WhatsApp message: the supplier's own names, in their packs, one line each. */
export const orderMessage = (order, branding) => {
  const name = branding?.profile?.nameEnglish || 'Perveen Aslam Memorial Hospital';
  const lines = sheetItems(order).map((i, idx) => {
    const item = i.vendorItemName || i.medicine?.name || '';
    const qty = i.packs != null
      ? packCount(i.packs, i.medicine?.supplierBottle, i.unitsPerPack || 1)
      : `${i.quantity} unit${Number(i.quantity) === 1 ? '' : 's'}`;
    return `${idx + 1}. ${item}: ${qty}`;
  });
  return [
    `*Purchase order ${order.orderNumber}*`,
    `From: ${name}`,
    `To: ${order.vendor?.vendorName || ''}`,
    `Date: ${sheetDate(order)}`,
    '',
    ...lines,
    '',
    `Please write order number ${order.orderNumber} on your bill, with the batch and expiry of every item. If an item is short, send what you have and say so on the bill.`,
  ].join('\n');
};

/**
 * A supplier's number as WhatsApp takes it, "923001234567", from however it was typed:
 * "+92-300-1234567", "0300 1234567", "00923001234567". Null when there is no number to use.
 */
export const whatsappNumber = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  else if (d.startsWith('0')) d = `92${d.slice(1)}`;
  else if (d.length === 10 && d.startsWith('3')) d = `92${d}`;
  return d.length >= 11 && d.length <= 15 ? d : null;
};

/** The chat with that number, the message typed in: WhatsApp Web on a computer, the app on a phone. */
export const chatUrl = (phone, text, phoneOrTablet = isPhoneOrTablet()) => (phoneOrTablet
  ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
  : `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(text)}`);

/** WhatsApp with no chat chosen, for a supplier with no number: the phone app offers its chat list. */
const pickChatUrl = (text, phoneOrTablet = isPhoneOrTablet()) => (phoneOrTablet
  ? `https://wa.me/?text=${encodeURIComponent(text)}`
  : 'https://web.whatsapp.com/');

/**
 * Opens the supplier's chat with the order typed out, then saves the order's PDF, and says what
 * happened through `notify(type, message)`. Call it straight from the click, with the order already
 * loaded: the browser lets a page open a tab only in answer to a click, not after it has waited.
 *
 * @returns {Promise<boolean>} false when the browser blocked the WhatsApp tab
 */
export async function shareOrderOnWhatsApp(order, branding, notify) {
  const text = orderMessage(order, branding);
  const phone = whatsappNumber(order.vendor?.contactNumber);
  const vendor = order.vendor?.vendorName || 'The supplier';
  // Without a number the message goes to whichever chat they pick, so it is copied to paste there.
  if (!phone) navigator.clipboard?.writeText(text).catch(() => {});
  const tab = window.open(phone ? chatUrl(phone, text) : pickChatUrl(text), WHATSAPP_TAB);
  if (!tab) {
    notify('warning', 'The browser blocked the WhatsApp tab. Allow pop-ups for this site and try again.');
    return false;
  }
  try { tab.opener = null; } catch { /* a WhatsApp tab already open is not ours to change */ }

  let pdf = true;
  try {
    await saveOrderPdf(order, branding);
  } catch (e) {
    console.error('Could not make the order PDF:', e);
    pdf = false;
  }
  const sheet = pdf
    ? 'The order sheet is in your Downloads as a PDF, to drag into the chat if they want it too.'
    : 'The PDF of the order sheet could not be made. Print the order if they want the sheet.';
  notify(pdf ? 'success' : 'warning', phone
    ? `${vendor}'s chat is open in WhatsApp with the order typed out. Press Send there. ${sheet}`
    : `${vendor} has no mobile number on file, so pick their chat in WhatsApp and paste the order, which is copied. ${sheet} Add their number under Vendors to go straight to their chat next time.`);
  return true;
}
